import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useProject } from '../../hooks/useProject';

interface ScheduleDetailPanelProps {
  jobNumber: string | null;
  onClose: () => void;
}

export function ScheduleDetailPanel({ jobNumber, onClose }: ScheduleDetailPanelProps) {
  const { data: project, isLoading } = useProject(jobNumber);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!jobNumber) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [jobNumber, onClose]);

  useEffect(() => {
    if (jobNumber) closeRef.current?.focus();
  }, [jobNumber]);

  if (!jobNumber) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/20 z-20"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Project detail"
        className="fixed right-0 top-0 h-full w-[480px] z-30 bg-white shadow-xl overflow-y-auto"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-brand-charcoal truncate">
            {isLoading ? 'Loading…' : project ? `${project.jobNumber} — ${project.name}` : jobNumber}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="text-slate-500 hover:text-slate-800 text-2xl leading-none ml-3"
          >
            ×
          </button>
        </header>

        {isLoading && (
          <div className="p-5 space-y-3 animate-pulse">
            <div className="h-5 bg-slate-100 rounded w-3/4" />
            <div className="h-4 bg-slate-100 rounded w-1/2" />
            <div className="h-32 bg-slate-100 rounded" />
          </div>
        )}

        {project && (
          <div className="p-5 space-y-4 text-sm">
            <div className="flex flex-wrap gap-2">
              {project.serviceLine && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">{project.serviceLine}</span>
              )}
              {project.account && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">{project.account}</span>
              )}
              {project.region && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">{project.region}</span>
              )}
              <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">
                {project.isBillable ? 'Billable' : 'Non-billable'}
              </span>
            </div>

            {(project.projectedStart || project.projectedEnd) && (
              <p className="text-slate-600">
                Projected {project.projectedStart ?? '?'} – {project.projectedEnd ?? '?'}
              </p>
            )}

            {project.boxFolderName && (
              <p>
                <span className="text-brand-blue hover:underline text-sm cursor-pointer">
                  Open in Box ↗
                </span>
              </p>
            )}

            <p className="text-xs text-slate-500 italic">
              This view is read-only. To update assignments, make changes in the source report and wait for the next ingest.
            </p>

            <div>
              <h3 className="text-sm font-semibold text-brand-charcoal mb-2">Crew Assignments</h3>
              {project.assignments.length === 0 ? (
                <p className="text-slate-500">No crew assignments on record for this project.</p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 text-left">
                      <tr>
                        <th className="py-2 px-3">Name</th>
                        <th className="py-2 px-3">Planned Dates</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.assignments.map((a, i) => {
                        // Assignments arrive sorted by person, so a repeat of the previous row's
                        // person is an additional stretch — show the name only once.
                        const isRepeat = i > 0 && project.assignments[i - 1].personId === a.personId;
                        return (
                          <tr
                            key={a.assignmentId}
                            className={isRepeat ? '' : 'border-t border-slate-100'}
                          >
                            <td className="py-2 px-3 font-medium text-brand-charcoal">
                              {isRepeat ? '' : a.personDisplayName}
                            </td>
                            <td className="py-2 px-3 text-slate-600">{a.plannedStart} – {a.plannedEnd}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Link
              to={`/projects/${project.jobNumber}`}
              className="text-brand-blue hover:underline text-sm"
              onClick={onClose}
            >
              View full project →
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
